import { useState, useEffect } from 'react';
import { Shield, Zap, TrendingUp, TrendingDown, ChevronDown, ChevronRight } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import PortfolioImpactPanel from '../components/PortfolioImpactPanel.jsx';
import TrackRecordPanel from '../components/TrackRecordPanel.jsx';
import { usePage } from '../contexts/PageContext.jsx';
import { usePageTracking } from '../hooks/usePageTracking.js';

function bandColor(pct) {
  if (pct >= 70) return '#10b981';
  if (pct >= 40) return '#f59e0b';
  return '#ef4444';
}

function bandLabel(pct) {
  if (pct >= 70) return 'high';
  if (pct >= 40) return 'neutral';
  return 'low';
}

function Sparkline({ points, width = 80, height = 20 }) {
  if (!points || points.length < 2) return <span className="text-[9px] text-korinth-muted">—</span>;
  const values = points.map(p => p.value_30d);
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 0.01;
  const stepX = width / (values.length - 1);
  const path = values.map((v, i) => `${i * stepX},${height - ((v - min) / range) * height}`).join(' L');
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline points={path} fill="none" stroke="#3b82f6" strokeWidth="1.5" />
    </svg>
  );
}

export default function CredibilityPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('credibility'); }, []);

  const [sources, setSources] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [expandedSignals, setExpandedSignals] = useState({});

  useEffect(() => {
    async function load() {
      try {
        const r = await fetch('/api/kairos/credibility');
        if (r.ok) {
          const d = await r.json();
          setSources(d.sources || []);
          setCycles(d.recent_emergency_cycles || []);
        }
      } catch {}
    }
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
  }, []);

  async function loadSignals(sourceName) {
    if (expandedSignals[sourceName]) return;
    try {
      const r = await fetch(`/api/kairos/credibility/${encodeURIComponent(sourceName)}/signals`);
      if (r.ok) {
        const data = await r.json();
        setExpandedSignals(s => ({ ...s, [sourceName]: data }));
      }
    } catch {}
  }

  const ranked = sources.filter(s => s.total_signals > 0);
  const unranked = sources.filter(s => !s.total_signals);
  const { impact, track, loading } = usePageTracking('credibility');
  const highCredCount = ranked.filter(s => (s.rolling_30d_hit_rate || 0) >= 0.7).length;
  const lowCredCount = ranked.filter(s => (s.rolling_30d_hit_rate || 0) < 0.4).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Source Credibility</h1>
        <span className="text-korinth-muted text-sm font-mono">THEMIS · Credibility Judge — {sources.length} tracked</span>
      </div>

      {/* Sprint 20: Summary strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">Tracked sources:</span> <span className="text-white font-bold">{sources.length}</span></span>
        <span><span className="text-korinth-muted">High credibility:</span> <span className="text-korinth-accent font-bold">{highCredCount}</span></span>
        <span><span className="text-korinth-muted">Low credibility:</span> <span className="text-korinth-danger font-bold">{lowCredCount}</span></span>
        <span><span className="text-korinth-muted">Recent emergency cycles:</span> <span className="text-korinth-warning font-bold">{cycles.length}</span></span>
      </div>

      {/* Sprint 21.5: THEMIS adjudication status banner */}
      <ThemisStatusBanner sources={sources} />

      {/* Sprint 20: Portfolio impact */}
      <PortfolioImpactPanel topic="Credibility" {...impact} loading={loading} />

      {/* Ranked sources */}
      <ModuleContainer title="Live Credibility Rankings" subtitle={`${ranked.length} sources with signal history`}>
        {ranked.length > 0 ? (
          <div className="space-y-1">
            {ranked.map((s) => {
              const pct = Math.round((s.rolling_30d_hit_rate || 0.5) * 100);
              const color = bandColor(pct);
              const trajectory = s.credibility_trajectory;
              const isExp = expanded === s.source_name;
              const signals = expandedSignals[s.source_name] || [];

              return (
                <div key={s.source_name} className="bg-korinth-bg rounded border border-korinth-border/30">
                  <button onClick={() => { setExpanded(isExp ? null : s.source_name); loadSignals(s.source_name); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left">
                    {isExp ? <ChevronDown className="w-3 h-3 text-korinth-muted" /> : <ChevronRight className="w-3 h-3 text-korinth-muted" />}
                    <span className="text-sm font-mono font-bold text-white w-48 truncate">{s.source_name}</span>
                    <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-korinth-border text-korinth-muted">{s.source_type}</span>
                    <div className="text-2xl font-mono font-bold" style={{ color }}>{pct}%</div>
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ color, backgroundColor: color + '15' }}>{bandLabel(pct)}</span>
                    <Sparkline points={trajectory} />
                    <span className="text-[10px] font-mono text-korinth-muted ml-auto">{s.total_signals} signals</span>
                    <span className="text-[9px] font-mono text-korinth-accent">✅ {s.corroborated_signals}</span>
                    <span className="text-[9px] font-mono text-korinth-danger">❌ {s.failed_signals}</span>
                    <span className="text-[9px] font-mono text-korinth-muted">⏱️ {s.pending_signals}</span>
                  </button>
                  {isExp && (
                    <div className="px-3 pb-3 border-t border-korinth-border/20 pt-2 space-y-1">
                      <div className="text-[10px] font-mono text-korinth-muted">
                        90d hit rate: {Math.round((s.rolling_90d_hit_rate || 0.5) * 100)}% · Last updated: {s.last_credibility_update?.slice(0, 16)}
                      </div>
                      {signals.length > 0 ? (
                        <div className="space-y-1 mt-2">
                          <div className="text-[9px] font-mono text-korinth-accent uppercase tracking-wider">Recent Signals</div>
                          {signals.slice(0, 10).map(sig => (
                            <div key={sig.id} className="flex items-start gap-2 px-2 py-1 bg-korinth-surface/50 rounded text-[10px] font-mono">
                              <span className={`w-3 ${sig.outcome === 'corroborated' ? 'text-korinth-accent' : sig.outcome === 'failed' ? 'text-korinth-danger' : 'text-korinth-muted'}`}>
                                {sig.outcome === 'corroborated' ? '✅' : sig.outcome === 'failed' ? '❌' : '⏱️'}
                              </span>
                              <span className="text-korinth-muted w-20 flex-shrink-0">{sig.observed_at?.slice(5, 16)}</span>
                              <span className="text-korinth-text flex-1">{sig.signal_text?.slice(0, 120) || '—'}</span>
                              <span className={`flex-shrink-0 ${sig.claimed_direction === 'bullish' ? 'text-korinth-accent' : sig.claimed_direction === 'bearish' ? 'text-korinth-danger' : ''}`}>
                                {sig.claimed_direction || '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : <div className="text-[10px] text-korinth-muted font-mono py-2">No signals recorded yet</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : <p className="text-[10px] text-korinth-muted font-mono text-center py-6">No credibility data yet — run seed script + credibilityAgent</p>}
      </ModuleContainer>

      {/* Unranked */}
      {unranked.length > 0 && (
        <ModuleContainer title="Awaiting Data" subtitle={`${unranked.length} sources seeded at neutral baseline (no signals yet)`}>
          <div className="flex gap-1 flex-wrap">
            {unranked.map(s => (
              <span key={s.source_name} className="text-[10px] font-mono px-2 py-1 rounded bg-korinth-bg border border-korinth-border/30 text-korinth-muted">
                {s.source_name} <span className="text-korinth-muted/50">({s.source_type})</span>
              </span>
            ))}
          </div>
        </ModuleContainer>
      )}

      {/* Emergency cycles */}
      <ModuleContainer title="Emergency Cycles" subtitle="Last 7 days — out-of-cadence KAIROS runs triggered by priority signals">
        {cycles.length > 0 ? (
          <div className="space-y-1">
            {cycles.map(c => (
              <div key={c.id} className="flex items-center gap-2 px-3 py-2 bg-korinth-bg rounded text-[10px] font-mono">
                <Zap className="w-3 h-3" style={{ color: c.status === 'completed' ? '#10b981' : c.status === 'suppressed' ? '#f59e0b' : '#ef4444' }} />
                <span className="text-korinth-muted w-20">{c.started_at?.slice(5, 16)}</span>
                <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-korinth-border text-korinth-text">L{c.trigger_priority}</span>
                <span className="text-white font-bold w-28 truncate">{c.trigger_source || 'system'}</span>
                <span className="text-korinth-text flex-1 truncate">{c.trigger_headline || '—'}</span>
                {c.status === 'completed' && (
                  <>
                    <span className="text-korinth-muted">{Math.round((c.duration_ms || 0) / 1000)}s</span>
                    <span className="text-korinth-accent">{c.observations_generated || 0} obs</span>
                    <span className="text-blue-400">{c.predictions_generated || 0} preds</span>
                  </>
                )}
                {c.status !== 'completed' && <span className="text-korinth-danger">{c.status}</span>}
              </div>
            ))}
          </div>
        ) : <p className="text-[10px] text-korinth-muted font-mono text-center py-4">No emergency cycles in the last 7 days</p>}
      </ModuleContainer>

      {/* Sprint 20: Track record */}
      <TrackRecordPanel {...track} loading={loading} metric_name="Credibility Engine" />
    </div>
  );
}

/**
 * Sprint 21.5: THEMIS adjudication status — makes it transparent when the
 * credibility engine runs + which sources are scored vs awaiting more data.
 */
function ThemisStatusBanner({ sources }) {
  const totalSources = sources.length;
  const gradedSources = sources.filter(s => (s.total_signals || 0) > 0);
  const baselineSources = sources.filter(s => {
    if (!s.total_signals) return true;
    // Sprint 16 convention: sources with <5 resolved signals still in baseline band
    const resolved = (s.correct_count || 0) + (s.wrong_count || 0);
    return resolved < 5;
  });

  // Next adjudication: THEMIS runs every 6h at :00 — compute time until next
  const now = new Date();
  const nextHour = Math.ceil(now.getUTCHours() / 6) * 6;
  const nextRun = new Date(now);
  nextRun.setUTCHours(nextHour % 24, 0, 0, 0);
  if (nextRun <= now) nextRun.setUTCDate(nextRun.getUTCDate() + 1);
  const hoursUntil = Math.max(0, Math.round((nextRun.getTime() - now.getTime()) / 3600000));

  const totalSignals = sources.reduce((s, src) => s + (src.total_signals || 0), 0);
  const gradedSignals = sources.reduce((s, src) => s + ((src.correct_count || 0) + (src.wrong_count || 0)), 0);

  return (
    <div className="bg-korinth-surface border-l-4 border-l-purple-400 border border-korinth-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-4 h-4 text-purple-400" />
        <span className="text-xs font-mono font-bold uppercase tracking-wider text-purple-400">
          THEMIS Adjudication Status
        </span>
        <span className="text-[10px] font-mono text-korinth-muted ml-auto">
          Runs every 6h · next in ~{hoursUntil}h
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px] font-mono">
        <div>
          <div className="text-[9px] text-korinth-muted uppercase tracking-wider">Grading progress</div>
          <div className="text-korinth-text mt-0.5">
            <span className="text-white font-bold">{gradedSignals}</span> of <span className="text-white font-bold">{totalSignals}</span> signals adjudicated
          </div>
        </div>
        <div>
          <div className="text-[9px] text-korinth-muted uppercase tracking-wider">Scored sources</div>
          <div className="text-korinth-text mt-0.5">
            <span className="text-korinth-accent font-bold">{gradedSources.length}</span> of {totalSources} have enough data for scoring
          </div>
        </div>
        <div>
          <div className="text-[9px] text-korinth-muted uppercase tracking-wider">Still baseline</div>
          <div className="text-korinth-text mt-0.5">
            <span className="text-korinth-warning font-bold">{baselineSources.length}</span> sources awaiting 5+ resolved signals
          </div>
        </div>
      </div>
      {baselineSources.length > 0 && (
        <div className="mt-2 text-[10px] font-mono text-korinth-muted leading-relaxed">
          Baseline sources (still aging): {baselineSources.slice(0, 8).map(s => s.source_name).join(', ')}{baselineSources.length > 8 ? `, +${baselineSources.length - 8} more` : ''}
        </div>
      )}
    </div>
  );
}
